/**
 * =======================================================================
 * ENVESEUR Custom Product Page JavaScript
 * =======================================================================
 * 
 * This module handles all custom product page functionality including:
 * - Variant selection and state management
 * - Dynamic pricing with VAT toggle
 * - Discount table integration (Dash Quantity Break Pricing)
 * - Stock level display
 * - Metafields rendering
 * - Dispatch countdown widget
 * - Print system / personalisation modal
 * - Large volume quote prefill
 * 
 * @version 2.0.0
 * @author ENVESEUR Development Team
 * 
 * Performance Optimizations:
 * - Deferred initialization for non-critical features
 * - Request guards to prevent race conditions
 * - Event delegation where possible
 * - Minimal DOM queries with caching
 * - Passive event listeners for scroll/touch
 */

(function() {
  'use strict';

  /* =====================================================================
     CONFIGURATION & CONSTANTS
  ===================================================================== */

  /**
   * Market IDs with special pricing rules
   * @constant {Set<string>}
   */
  const WHOLE_AMOUNT_MARKETS = new Set([
    '31489491008', // Hungary - uses integer totals with .50 rounding
  ]);

  /**
   * Spanish national bank holidays (MM-DD format)
   * Used for dispatch countdown visibility
   * @constant {string[]}
   */
  const SPANISH_HOLIDAYS = [
    '01-01', '01-06', '05-01', '08-15', '09-11',
    '10-12', '11-01', '12-06', '12-08', '12-25'
  ];

  /* =====================================================================
     STATE MANAGEMENT
  ===================================================================== */

  /**
   * Application state object - centralized state management
   * @type {Object}
   */
  const state = {
    // VAT & Pricing
    includeVAT: true,
    vatRate: 0,
    originalSingleUnitPriceExVAT: 0,
    originalComparePriceWithVAT: 0,

    // Currency formatting
    currencySymbol: '',
    currencySymbolPlacement: 'after',
    decimalSeparator: '.',
    thousandSeparator: ',',

    // Market & product info
    marketId: '',
    currencyIsoCode: 'EUR',

    // Variant management
    discountBrackets: [],

    // Print system
    printSystemIframeUrlSrc: '',
  };

  /* =====================================================================
     DOM SELECTORS (Cached & Lazy)
  ===================================================================== */

  /**
   * DOM helper functions that always query live nodes
   * Shopify may replace parts of the section dynamically
   */
  const DOM = {
    /** @returns {NodeListOf<Element>} Option selector fieldsets */
    getOptionSelectors: () => document.querySelectorAll('.product .option-selector'),

    /** @returns {Element|null} Discount table element */
    getDiscountTable: () => document.querySelector('.product .qty-break-table'),

    /** @returns {Element|null} Product SKU display element */
    getProductSku: () => document.querySelector('.product .product-sku'),

    /** @returns {HTMLIFrameElement|null} Personalisation iframe */
    getPersonaliseIframe: () => document.getElementById('personalise-product-iframe'),

    /** @returns {HTMLInputElement|null} Print system URL input */
    getPrintSystemUrlInput: () => document.getElementById('print-system-iframe-url'),

    /** @returns {HTMLInputElement|null} Main quantity input (excludes quickbuy) */
    getQuantityInput: () => document.querySelector('.product-info .qty-input__input'),

    /** @returns {Element|null} Current price element */
    getPriceCurrent: () => document.querySelector('.price__current'),

    /** @returns {Element|null} Compare/was price element */
    getPriceWas: () => document.querySelector('.price__was'),

    /** @returns {HTMLInputElement|null} Hidden variant ID input */
    getVariantIdInput: () => document.getElementById('product-variant_id__value'),
  };

  /* =====================================================================
     INITIALIZATION
  ===================================================================== */

  /**
   * Main initialization - runs on DOMContentLoaded
   * Order of operations is important for correct state setup
   */
  async function init() {
    // 1. Parse configuration from DOM (synchronous, critical)
    parseConfiguration();

    // 2. Initialize pricing (synchronous, critical for LCP)
    initializePrices();

    // 3. Hydrate market-resolved price (async, non-blocking)
    // Shopify Markets catalog prices are NOT resolved in Liquid `variant.price`
    // on some multi-market Plus stores; cart-side pricing IS resolved.
    // We probe /cart.js (or add+remove) to fetch the true market price and
    // patch the DOM + state. See snippets/price.liquid for the un-resolved source.
    // Fire and forget on initial load; variant-change path awaits it.
    hydrateMarketResolvedPrice().then(() => {
      // Once hydration finishes, also arm the qty-break observer so any
      // late re-renders from the app are corrected.
      watchDiscountTableForReRender();
    });

    // 4. Initialize VAT toggle (synchronous, critical)
    initializeVATToggle();

    // 5. Initialize variant change handling (synchronous, critical)
    initVariantChangeHandler();

    // 6. Initialize quantity input handler (synchronous)
    initQuantityInputHandler();

    // 7. Deferred: Non-critical initializations (improves LCP)
    requestIdleCallback(() => {
      initCustomTabs();
      initLargeVolumeQuotePrefill();
      initDispatchCountdown();
    }, { timeout: 2000 });
  }

  /**
   * Parse configuration values from hidden inputs in the DOM
   */
  function parseConfiguration() {
    // VAT rate (convert from percentage to decimal)
    const vatInput = document.getElementById('current-market-vat-rate');
    if (vatInput) {
      const rawVAT = parseFloat(vatInput.value);
      state.vatRate = isNaN(rawVAT) ? 0 : rawVAT / 100;
    }

    // Market ID
    const marketEl = document.getElementById('store-market-id');
    state.marketId = marketEl?.value || '';

    // Currency ISO code
    const currencyEl = document.getElementById('store-currency-iso-code');
    state.currencyIsoCode = currencyEl?.value || 'EUR';
  }

  /**
   * Initialize original prices from DOM
   * Theme shows EX-VAT by default, so we parse and store both bases
   */
  function initializePrices() {
    const priceCurrentEl = DOM.getPriceCurrent();

    if (priceCurrentEl) {
      const priceText = priceCurrentEl.querySelector('.js-value')?.textContent?.trim() || '';

      // Extract currency symbol (non-numeric characters)
      state.currencySymbol = priceText.replace(/[0-9.,\s\u00A0]/g, '') || '';

      // Determine symbol placement
      state.currencySymbolPlacement = determineCurrencyPlacement(priceText, state.currencySymbol);

      // Parse EX-VAT price (theme default)
      const parsedExVAT = parsePrice(priceText);
      state.originalSingleUnitPriceExVAT = parsedExVAT;

      // Store globally for compatibility
      window.originalSingleUnitPriceExVAT = parsedExVAT;
    }

    // Compare/was price
    const priceWasEl = DOM.getPriceWas();
    if (priceWasEl) {
      const wasText = priceWasEl.textContent?.trim() || '';
      const wasExVAT = parsePrice(wasText);
      // Store as WITH VAT for consistency
      state.originalComparePriceWithVAT = state.vatRate > 0 
        ? wasExVAT * (1 + state.vatRate) 
        : wasExVAT;
    }
  }

  /**
   * Synchronously apply a cached market price (if any) to DOM/state for the
   * given variant id. Returns true if a cache hit was applied.
   * Used on variant-change to avoid blocking on async hydration when the
   * variant was already resolved in this session.
   * @param {string|number} variantId
   * @returns {boolean}
   */
  function applyCachedMarketPriceSync(variantId) {
    if (!variantId) return false;
    const cacheKey = `envMarketPrice_${state.marketId}_${state.currencyIsoCode}_${variantId}`;
    let priceUnits = null;
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached.price === 'number' && cached.currency === state.currencyIsoCode) {
          priceUnits = cached.price;
        }
      }
    } catch (_) { /* ignore */ }
    if (priceUnits == null) return false;

    // Patch state
    state.originalSingleUnitPriceExVAT = priceUnits;
    window.originalSingleUnitPriceExVAT = priceUnits;

    // Patch DOM
    const priceCurrentEl = DOM.getPriceCurrent();
    const jsValue = priceCurrentEl?.querySelector('.js-value');
    if (jsValue) {
      // Only overwrite if it visibly differs (avoids redundant DOM writes)
      const current = parsePrice(jsValue.textContent || '');
      if (Math.abs(current - priceUnits) >= 0.005) {
        jsValue.textContent = formatPrice(priceUnits);
      }
    }
    return true;
  }

  /**
   * Fetch market-resolved price for current variant and patch DOM/state.
   *
   * READ-ONLY hydration only. We never POST to /cart/add.js because:
   *   1. Shopify rate-limits cart endpoints aggressively (429s break apps
   *      that monkey-patch fetch).
   *   2. Background probes race with user Add-to-Cart actions (409s).
   *   3. The displayed mismatch is purely cosmetic - the cart prices
   *      correctly regardless. (See screenshot: HUF cart shows 26 Ft per
   *      unit, line total 26.190 Ft; both correct.)
   *
   * Resolution order:
   *   1. sessionStorage cache (keyed by market + currency + variant)
   *   2. /cart.js read (zero-mutation; only succeeds if variant is
   *      already in the cart from a previous user action)
   *
   * Variants not in the cart will display the Liquid base price. That is
   * accepted as a cosmetic limitation of Shopify Markets catalogs.
   */
  async function hydrateMarketResolvedPrice() {
    const variant = getSelectedVariant();
    const variantId = variant?.id || DOM.getVariantIdInput()?.value;
    if (!variantId) return;

    const cacheKey = `envMarketPrice_${state.marketId}_${state.currencyIsoCode}_${variantId}`;
    let priceUnits = null;

    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached.price === 'number' && cached.currency === state.currencyIsoCode) {
          priceUnits = cached.price;
        }
      }
    } catch (_) { /* ignore */ }

    if (priceUnits == null) {
      try {
        // Read-only path: variant already in cart -> use that price.
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const existing = Array.isArray(cart.items)
            ? cart.items.find(i => String(i.variant_id) === String(variantId))
            : null;
          if (existing && typeof existing.price === 'number') {
            priceUnits = existing.price / 100;
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify({
                price: priceUnits,
                currency: state.currencyIsoCode,
                ts: Date.now(),
              }));
            } catch (_) { /* storage quota */ }
          }
        }
      } catch (err) {
        // Silent: hydration failure just falls back to Liquid base price.
        return;
      }
    }

    if (priceUnits == null) return;

    // No change needed if Liquid already returned the market-resolved price
    if (Math.abs(priceUnits - state.originalSingleUnitPriceExVAT) < 0.005) return;

    // Patch state
    state.originalSingleUnitPriceExVAT = priceUnits;
    window.originalSingleUnitPriceExVAT = priceUnits;

    // Patch DOM (the .price__current .js-value node is the visible source-of-truth)
    const priceCurrentEl = DOM.getPriceCurrent();
    const jsValue = priceCurrentEl?.querySelector('.js-value');
    if (jsValue) {
      jsValue.textContent = formatPrice(priceUnits);
    }

    // Recompute derived UI (VAT-applied total, discount table tiers)
    try {
      if (typeof updatePrice === 'function') updatePrice();
      if (typeof updateDiscountTablePrices === 'function') updateDiscountTablePrices();
    } catch (_) { /* best effort */ }
  }

  // NOTE: prefetchVariantMarketPrices / probeVariantMarketPrice were removed.
  // They caused 429 (rate-limit) and 409 (race) errors that broke the
  // cart drawer (third-party apps that monkey-patch fetch choke on HTML
  // 429 responses).
  // The displayed-vs-charged mismatch on uncached variants is accepted as
  // a cosmetic Shopify-Markets limitation. The cart always charges the
  // correct market-resolved price.

  /* =====================================================================
     VARIANT MANAGEMENT
  ===================================================================== */

  /**
   * Get the currently selected variant from option selectors
   * Supports both radio buttons and dropdown selectors
   * @returns {Object|null} Variant object or null
   */
  function getSelectedVariant() {
    const optionSelectors = DOM.getOptionSelectors();

    // Try to get current variant from variant-picker script
    // Theme outputs the current selected variant as a single JSON object
    const script = document.querySelector('variant-picker script[type="application/json"]');
    
    if (script) {
      try {
        const data = JSON.parse(script.textContent);
        // The theme outputs current variant directly as { id, sku, options, ... }
        if (data && data.id && data.sku) {
          return data;
        }
        // Fallback for array structure (some themes)
        const variants = data?.product?.variants || data?.variants || [];
        if (variants.length > 0) {
          // Get selected options from UI and find matching variant
          const selectedOptions = Array.from(optionSelectors).map(selector => {
            if (selector.dataset.selectorType === 'dropdown') {
              const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
              return selected?.dataset.value || null;
            }
            return selector.querySelector('input:checked')?.value || null;
          });
          
          if (selectedOptions.length && !selectedOptions.some(v => v == null)) {
            const match = variants.find(v => 
              Array.isArray(v.options) && 
              v.options.every((opt, i) => opt === selectedOptions[i])
            );
            if (match) return match;
          }
          return variants[0];
        }
      } catch (e) {
        console.error('[ProductPage] Failed to parse variant JSON:', e);
      }
    }

    // Fallback: use DOM elements (for single-variant products or when no variant-picker)
    const sku = document.querySelector('.product-sku__value')?.textContent?.trim();
    const id = DOM.getVariantIdInput()?.value;
    return (sku && id) ? { sku, id } : null;
  }

  /**
   * Handle variant change event
   * Updates UI, prices, stock levels, and metafields
   */
  async function handleVariantChange() {
    // Reorder measurement options for better UX
    autoReorderMeasureFieldsets();

    const productSkuEl = DOM.getProductSku();
    const hasOptions = DOM.getOptionSelectors().length > 0;

    // Get selected variant
    const variant = getSelectedVariant();

    // Update hidden variant ID input
    if (variant?.id) {
      const hiddenInput = DOM.getVariantIdInput();
      if (hiddenInput) hiddenInput.value = String(variant.id);
    }

    // Handle valid variant with SKU
    if (variant?.sku) {
      handleValidVariant(variant, productSkuEl, hasOptions);
    } else {
      handleInvalidVariant(productSkuEl);
    }
  }

  /**
   * Handle a valid variant selection
   * @param {Object} variant - The selected variant
   * @param {Element|null} productSkuEl - SKU display element
   * @param {boolean} hasOptions - Whether product has options
   */
  function handleValidVariant(variant, productSkuEl, hasOptions) {
    productSkuEl?.classList.remove('blurred');

    // The specifications table is re-rendered server-side (already localized)
    // by the variant-picker section swap, so just clear any stale blur state.
    document.querySelectorAll('.product-metafields-container')
      .forEach(c => c.classList.remove('blurred'));

    // Update personalisation iframe URL
    updatePersonaliseIframeSrc(variant.sku);

    // Enable UI elements
    updateUIForValidVariant();

    // Reset quantity input
    const qtyInput = DOM.getQuantityInput();
    if (qtyInput) qtyInput.value = 1;

    // Clear discount brackets for fresh calculation
    state.discountBrackets = [];

    // Reinitialize after DOM updates
    setTimeout(() => {
      initializePrices();

      // Apply cached market price synchronously (instant). Most variant
      // switches after the first session visit will hit this path.
      const cacheHit = applyCachedMarketPriceSync(variant.id);

      // If we don't have a cached price, kick off hydration in the
      // background. The discount-table observer will re-run our corrections
      // whenever the app re-renders the tier table with fresh data.
      if (!cacheHit) {
        hydrateMarketResolvedPrice().then(() => {
          // Re-apply after hydration settles
          updateDiscountTablePrices();
          updatePrice();
        });
      }

      initializeVATToggle();
      initCustomTabs();

      // Setup discount table when ready
      initDiscountTableWhenReady(() => {
        updateDiscountTablePrices();
        updatePrice();
        initQuantityInputHandler();

        const qty = parseInt(DOM.getQuantityInput()?.value, 10) || 1;
        highlightPriceBreak(qty);

        // The qty-break app may re-render the discount table asynchronously
        // after a variant change (it fetches tier data per variant). Watch
        // for those mutations and re-apply our VAT-toggled prices.
        watchDiscountTableForReRender();
      });
    }, 0);
  }

  /**
   * Handle an invalid variant selection (no SKU)
   * @param {Element|null} productSkuEl 
   */
  function handleInvalidVariant(productSkuEl) {
    clearPersonaliseIframeSrc();
    updateUIForInvalidVariant();
    productSkuEl?.classList.add('blurred');

    document.querySelectorAll('.product-metafields-container')
      .forEach(c => c.classList.add('blurred'));
  }

  /**
   * Update UI for valid variant
   */
  function updateUIForValidVariant() {
    DOM.getDiscountTable()?.classList.remove('blurred');
    document.querySelector('#personalise-product-btn')?.classList.remove('disabled');
  }

  /**
   * Update UI for invalid variant
   */
  function updateUIForInvalidVariant() {
    DOM.getDiscountTable()?.classList.add('blurred');
    document.querySelector('#personalise-product-btn')?.classList.add('disabled');
    state.discountBrackets = [];
    highlightPriceBreak(null);
  }

  /**
   * Initialize variant change handler
   */
  function initVariantChangeHandler() {
    // Listen for theme's variant change event
    document.addEventListener('on:variant:change', () => {
      // Wait for theme to finish section replacement
      setTimeout(handleVariantChange, 0);
    });

    // Fallback for older themes without variant-picker
    if (!document.querySelector('variant-picker')) {
      DOM.getOptionSelectors().forEach(fieldset => {
        fieldset.querySelectorAll('input').forEach(input => {
          input.addEventListener('change', handleVariantChange);
        });
      });
    }

    // Initial sync
    handleVariantChange();
  }

  /* =====================================================================
     DISCOUNT TABLE
  ===================================================================== */

  /**
   * Initialize discount table when ready (may be dynamically loaded)
   * @param {Function} callback - Called when table is ready
   */
  function initDiscountTableWhenReady(callback) {
    const table = DOM.getDiscountTable();

    if (table) {
      setupDiscountTable();
      callback?.();
      return;
    }

    // Observe for table injection
    const observer = new MutationObserver(() => {
      if (!DOM.getDiscountTable()) return;
      observer.disconnect();
      setupDiscountTable();
      callback?.();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Setup discount table (brackets, click handlers)
   */
  function setupDiscountTable() {
    buildDiscountBrackets();
    initRowClickToSetQuantity();
  }

  /**
   * Watch the qty-break discount table for content changes (the app may
   * re-render tiers asynchronously on variant change) and re-apply our
   * VAT-toggled tier prices. Debounced to avoid feedback loops with our
   * own writes.
   */
  let _discountTableObserver = null;
  let _discountTableReapplyTimer = null;
  function watchDiscountTableForReRender() {
    const table = DOM.getDiscountTable();
    if (!table) return;

    // Disconnect previous observer (variant switches)
    if (_discountTableObserver) {
      _discountTableObserver.disconnect();
      _discountTableObserver = null;
    }

    _discountTableObserver = new MutationObserver(mutations => {
      // Only react to genuine app re-renders (new rows or changed data-break-*
      // attrs), not our own <span class="money"> writes inside .qty-break-price.
      const appReRender = mutations.some(m => {
        if (m.type === 'childList') {
          return Array.from(m.addedNodes).some(n =>
            n.nodeType === 1 && (
              n.classList?.contains('qty-break-table-row')
              || n.querySelector?.('.qty-break-table-row')
            )
          );
        }
        if (m.type === 'attributes') {
          return m.attributeName?.startsWith('data-break-');
        }
        return false;
      });
      if (!appReRender) return;

      clearTimeout(_discountTableReapplyTimer);
      _discountTableReapplyTimer = setTimeout(() => {
        buildDiscountBrackets();
        initRowClickToSetQuantity();
        updateDiscountTablePrices();
        updatePrice();
        const qty = parseInt(DOM.getQuantityInput()?.value, 10) || 1;
        highlightPriceBreak(qty);
      }, 30);
    });

    _discountTableObserver.observe(table, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-break-min', 'data-break-max', 'data-break-quantity'],
    });
  }

  /**
   * Build discount brackets array from the qty-break rows.
   *
   * The Dash Quantity Break Pricing app exposes the bracket boundaries via
   * `data-break-min` (inclusive) and `data-break-max` (exclusive upper bound;
   * empty string means unbounded). Each row's `.qty-break-price` cell holds
   * the rendered EX-VAT catalog price for that tier - we read it verbatim,
   * no derived math required.
   */
  function buildDiscountBrackets() {
    state.discountBrackets = [];
    const table = DOM.getDiscountTable();
    if (!table) return;

    table.querySelectorAll('.qty-break-table-row').forEach(row => {
      const priceCell = row.querySelector('.qty-break-price');
      if (!priceCell) return;

      const minAttr = row.getAttribute('data-break-min');
      const maxAttr = row.getAttribute('data-break-max');
      const min = parseInt(minAttr, 10);
      if (!Number.isFinite(min) || min <= 0) return;

      // data-break-max is the exclusive upper bound (e.g. min=1, max=100 means
      // the bracket covers 1-99). Convert to an inclusive max to match the rest
      // of the codebase (getDiscountBracket uses `quantity <= b.max`).
      const exclusiveMax = parseInt(maxAttr, 10);
      const max = Number.isFinite(exclusiveMax) ? exclusiveMax - 1 : Infinity;

      // Source of truth for the tier's EX-VAT unit price. The Dash app stamps
      // the raw (unrounded) value on `data-unit-price`, which may carry more
      // than 2 decimals for special customer pricing (e.g. 0.0144). We read it
      // verbatim so both the precision and the intended decimal count are
      // preserved. Fallbacks: our own `data-ex-vat-origin` stamp, then the
      // rendered text (which the app only prints to 2 decimals).
      const rawUnitPrice = priceCell.getAttribute('data-unit-price');
      const rawUnitNum = parseFloat(rawUnitPrice);
      let originalPrice;
      let priceDecimals;

      if (rawUnitPrice != null && rawUnitPrice.trim() !== '' && Number.isFinite(rawUnitNum)) {
        originalPrice = rawUnitNum;
        priceDecimals = clampTierDecimals(countDecimalPlaces(rawUnitPrice));
      } else {
        const stampedRaw = priceCell.getAttribute('data-ex-vat-origin') || '';
        const stampedExVAT = parseFloat(stampedRaw);
        if (Number.isFinite(stampedExVAT) && stampedExVAT > 0) {
          originalPrice = stampedExVAT;
          priceDecimals = clampTierDecimals(countDecimalPlaces(stampedRaw));
        } else {
          const priceText = priceCell.querySelector('.money')?.textContent?.trim()
            || priceCell.textContent?.trim()
            || '0';
          originalPrice = parsePrice(priceText);
          priceDecimals = clampTierDecimals(countDecimalPlaces(priceText));
        }
      }

      state.discountBrackets.push({
        min,
        max,
        originalPrice,
        decimals: priceDecimals,
        rowElement: row,
        priceCellElement: priceCell,
      });
    });

    state.discountBrackets.sort((a, b) => a.min - b.min);
  }

  /**
   * Get discount bracket for a given quantity
   * @param {number} quantity 
   * @returns {Object|null}
   */
  function getDiscountBracket(quantity) {
    return state.discountBrackets.find(b => 
      quantity >= b.min && (quantity <= b.max || b.max === Infinity)
    ) || null;
  }

  /**
   * Highlight the price break row for current quantity
   * @param {number|null} quantity 
   */
  function highlightPriceBreak(quantity) {
    document.querySelectorAll('.qty-break-table .qty-break-table-row')
      .forEach(r => r.classList.remove('current-price-break'));

    if (quantity == null || quantity <= 0 || !state.discountBrackets.length) return;

    const bracket = getDiscountBracket(quantity);
    bracket?.rowElement?.classList.add('current-price-break');
  }

  /**
   * Make table rows clickable to set quantity
   */
  function initRowClickToSetQuantity() {
    const qtyInput = DOM.getQuantityInput();
    if (!state.discountBrackets.length || !qtyInput) return;

    state.discountBrackets.forEach(bracket => {
      const row = bracket.rowElement;
      if (!row || row.dataset.listenerAdded) return;

      row.style.cursor = 'pointer';
      row.addEventListener('click', () => {
        const liveInput = DOM.getQuantityInput();
        if (!liveInput) return;

        liveInput.value = bracket.min;
        liveInput.dispatchEvent(new Event('input', { bubbles: true }));
      });

      row.dataset.listenerAdded = 'true';
    });
  }

  /* =====================================================================
     PRICE CALCULATIONS
  ===================================================================== */

  /**
   * Resolve the EX-VAT unit price the cart will actually charge for a tier.
   *
   * When a currency has no fixed tier prices in Dash, the app converts each
   * base-currency tier with the raw FX rate (e.g. EUR 0.23 -> 1.7536 kr), but
   * Shopify presents the variant at its rounded price (1.75 kr) and Dash's
   * discount scales that price by tier/base. So the cart charges
   * 1.75 x (1.1437 / 1.7536) = 1.1413 kr, not the 1.1437 kr in the table.
   *
   * The adjustment only applies when the 1+ tier rounds to (but differs from)
   * the variant's presentment price, i.e. an auto-converted catalogue. Fixed
   * per-currency prices and special customer pricing are returned unchanged.
   * The tier ratio is computed on the base-currency prices recovered via
   * Shopify.currency.rate (exact), falling back to the app's 4-decimal
   * presentment values (can be a fraction of a cent off).
   * @param {Object} bracket
   * @returns {number}
   */
  function getChargedTierUnitPrice(bracket) {
    const tierPrice = bracket.originalPrice;
    const variantPrice = state.originalSingleUnitPriceExVAT || window.originalSingleUnitPriceExVAT || 0;
    const firstTier = state.discountBrackets.find(b => b.min === 1);
    const firstTierPrice = firstTier?.originalPrice || 0;

    if (!(tierPrice > 0) || !(variantPrice > 0) || !(firstTierPrice > 0)) return tierPrice;
    if (firstTierPrice === variantPrice) return tierPrice;
    if (Math.abs(roundHalfUp(firstTierPrice, 2) - variantPrice) >= 0.005) return tierPrice;

    const tierBase = toBaseCurrencyPrice(tierPrice);
    const firstTierBase = toBaseCurrencyPrice(firstTierPrice);
    const ratio = (tierBase > 0 && firstTierBase > 0)
      ? tierBase / firstTierBase
      : tierPrice / firstTierPrice;

    return variantPrice * ratio;
  }

  /**
   * Convert an auto-converted presentment price back to the shop's base
   * currency using Shopify's storefront rate. Base tiers are normally set to
   * 2 decimals (up to 4 for special pricing), so the result is snapped to the
   * nearest 2dp value when within the presentment rounding noise.
   * @param {number} presentmentPrice
   * @returns {number|null} null when no usable rate is available
   */
  function toBaseCurrencyPrice(presentmentPrice) {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    if (!(rate > 0) || rate === 1) return null;

    const base = presentmentPrice / rate;
    const base2dp = roundHalfUp(base, 2);
    return Math.abs(base - base2dp) < 0.0005 ? base2dp : roundHalfUp(base, 4);
  }

  /**
   * Update discount table prices with VAT toggle.
   *
   * Tier prices come pre-resolved (correct per market catalog) from the
   * qty-break app, so all we do here is apply the VAT toggle and the
   * market-specific rounding policy, then write the result back into the
   * `.qty-break-price` cell.
   */
  function updateDiscountTablePrices() {
    if (!state.discountBrackets.length) return;

    const mId = state.marketId;

    state.discountBrackets.forEach(bracket => {
      const cell = bracket.priceCellElement;
      if (!cell || !(bracket.originalPrice > 0)) return;

      const unitExVAT = getChargedTierUnitPrice(bracket);
      // Follow the decimal count the merchant sent (2 standard, up to 4 for
      // special customer pricing). Whole-amount markets keep integer prices.
      const decimals = bracket.decimals || 2;
      let effectiveUnit;
      if (state.includeVAT && state.vatRate > 0) {
        effectiveUnit = unitExVAT * (1 + state.vatRate);
      } else {
        effectiveUnit = unitExVAT;
      }

      // Stamp the EX-VAT origin so subsequent re-reads don't compound VAT.
      // The qty-break app wipes this attribute on re-render (innerHTML reset),
      // which is exactly the boundary where we want a fresh read from the new
      // catalog price.
      cell.setAttribute('data-ex-vat-origin', String(bracket.originalPrice));

      if (isWholeAmountMarket(mId)) {
        cell.innerHTML = `<span class="money">${formatPrice(roundHalfUpToInteger(effectiveUnit), 0)}</span>`;
      } else {
        cell.innerHTML = `<span class="money">${formatPrice(roundHalfUp(effectiveUnit, decimals), decimals)}</span>`;
      }
    });
  }

  /**
   * Update main product price display
   */
  function updatePrice() {
    const priceCurrentEl = DOM.getPriceCurrent();
    if (!priceCurrentEl) return;

    const qtyInput = DOM.getQuantityInput();
    const quantity = Math.max(parseInt(qtyInput?.value || '1', 10), 1);
    const mId = state.marketId;

    // Resolve unit price for the active bracket. If no bracket matches
    // (e.g. table not yet rendered) fall back to the single-unit base price.
    const bracket = getDiscountBracket(quantity);
    const baseExVAT = state.originalSingleUnitPriceExVAT || window.originalSingleUnitPriceExVAT || 0;
    const unitExVAT = (bracket && bracket.originalPrice > 0)
      ? getChargedTierUnitPrice(bracket)
      : baseExVAT;

    // Multiply the FULL-precision unit price by quantity, then round the
    // resulting subtotal to 2dp. Special customer prices can carry up to 4
    // decimals (e.g. 0.0144), and the cart computes the line total from that
    // full-precision unit (0.0144 x 1000 = 14.40). Pre-rounding the unit to
    // 2dp would make the displayed total drift from what the cart charges.
    const subtotalExVAT = roundHalfUp(unitExVAT * quantity, 2);

    // Apply VAT if enabled
    let total;
    if (state.includeVAT && state.vatRate > 0) {
      const vatAmount = roundHalfUp(subtotalExVAT * state.vatRate, 2);
      total = subtotalExVAT + vatAmount;
    } else {
      total = subtotalExVAT;
    }

    // Update display
    const valueEl = priceCurrentEl.querySelector('.js-value');
    if (valueEl) {
      valueEl.textContent = isWholeAmountMarket(mId)
        ? formatPrice(roundHalfUpToInteger(total), 0)
        : formatPrice(total, 2);
    }

    // Update compare/was price
    updateComparePrice(quantity, mId);
  }

  /**
   * Update compare/was price.
   *
   * The qty-break app does not expose a per-tier compare-at price, so we
   * use the product's (single-unit) compare-at value rendered by Liquid
   * and scale it by quantity. Same VAT toggle and rounding rules as the
   * main price.
   * @param {number} quantity
   * @param {string} mId
   */
  function updateComparePrice(quantity, mId) {
    const priceWasEl = DOM.getPriceWas();
    if (!priceWasEl || state.originalComparePriceWithVAT <= 0) return;

    const compareWithVAT = state.originalComparePriceWithVAT;
    const baseCompareExVAT = state.vatRate > 0
      ? compareWithVAT / (1 + state.vatRate)
      : compareWithVAT;

    const compareUnitExVAT = roundHalfUp(baseCompareExVAT, 2);
    const compareSubtotalExVAT = roundHalfUp(compareUnitExVAT * quantity, 2);

    let compareTotal;
    if (state.includeVAT && state.vatRate > 0) {
      const compareVAT = roundHalfUp(compareSubtotalExVAT * state.vatRate, 2);
      compareTotal = compareSubtotalExVAT + compareVAT;
    } else {
      compareTotal = compareSubtotalExVAT;
    }

    const valueEl = priceWasEl.querySelector('.js-value');
    if (valueEl) {
      valueEl.textContent = isWholeAmountMarket(mId)
        ? formatPrice(roundHalfUpToInteger(compareTotal), 0)
        : formatPrice(compareTotal, 2);
    }
  }

  /* =====================================================================
     VAT TOGGLE
  ===================================================================== */

  /**
   * Initialize VAT toggle checkbox and sync with cookie
   */
  function initializeVATToggle() {
    const checkbox = document.getElementById('show-vat-checkbox');
    if (!checkbox) return;

    // Check if already bound
    if (checkbox.dataset.vatToggleBound === 'true') {
      // Just sync state
      const cookie = getCookie('includeVAT');
      state.includeVAT = cookie === null ? true : cookie === 'true';
      checkbox.checked = state.includeVAT;
      updateVATLabels(state.includeVAT);
      return;
    }

    checkbox.dataset.vatToggleBound = 'true';

    // Initialize from cookie
    const cookie = getCookie('includeVAT');
    state.includeVAT = cookie === null ? true : cookie === 'true';
    setCookie('includeVAT', state.includeVAT, 30);
    checkbox.checked = state.includeVAT;

    updateVATLabels(state.includeVAT);

    // Listen for changes
    checkbox.addEventListener('change', () => {
      state.includeVAT = checkbox.checked;
      setCookie('includeVAT', state.includeVAT, 30);
      updateVATLabels(state.includeVAT);
      updateDiscountTablePrices();
      updatePrice();
    });
  }

  /**
   * Update VAT label visibility (EX VAT / INC VAT superscripts)
   * @param {boolean} includeVAT 
   */
  function updateVATLabels(includeVAT) {
    const showLabels = state.vatRate > 0;

    document.querySelectorAll('.product-info__ex-vat_sup')
      .forEach(n => n.classList.toggle('custom-hidden', includeVAT || !showLabels));

    document.querySelectorAll('.product-info__inc-vat_sup')
      .forEach(n => n.classList.toggle('custom-hidden', !includeVAT || !showLabels));
  }

  /* =====================================================================
     QUANTITY INPUT
  ===================================================================== */

  /**
   * Initialize quantity input change handler
   */
  function initQuantityInputHandler() {
    const qtyInput = DOM.getQuantityInput();
    if (!qtyInput || qtyInput.dataset.priceHandlerBound === 'true') return;

    const handleQuantityChange = () => {
      const quantity = parseInt(qtyInput.value, 10);
      highlightPriceBreak(isNaN(quantity) ? null : quantity);
      updatePrice();
    };

    // Typing fires `input`; the theme's +/- buttons only dispatch `change`
    qtyInput.addEventListener('input', handleQuantityChange);
    qtyInput.addEventListener('change', handleQuantityChange);

    qtyInput.dataset.priceHandlerBound = 'true';

    // Initial highlight
    const initialQty = parseInt(qtyInput.value, 10);
    if (!isNaN(initialQty)) {
      highlightPriceBreak(initialQty);
    }
  }

  /* =====================================================================
     PERSONALISE / PRINT SYSTEM
  ===================================================================== */

  /**
   * Update personalise iframe source URL
   * @param {string} sku 
   */
  function updatePersonaliseIframeSrc(sku) {
    const urlInput = DOM.getPrintSystemUrlInput();
    if (urlInput) {
      state.printSystemIframeUrlSrc = urlInput.value + sku;
    }
  }

  /**
   * Clear personalise iframe source
   */
  function clearPersonaliseIframeSrc() {
    const iframe = DOM.getPersonaliseIframe();
    if (iframe) iframe.src = '';
  }

  // Inject translated label into third-party personalise button (#pp_customize_design_btn)
  (function injectPersonaliseLabel() {
    const LABEL_INPUT_ID = 'personalise-product-label';
    const TARGET_ID = 'pp_customize_design_btn';

    function applyLabel() {
      const label = document.getElementById(LABEL_INPUT_ID)?.value;
      const btn = document.getElementById(TARGET_ID);
      if (btn && label) {
        btn.textContent = label;
        return true;
      }
      return false;
    }

    function relocateContainer() {
      const container = document.getElementById('pp_main_btn_sec');
      const anchor = document.getElementById('custom-dispatch-countdown-widget');
      if (!container || !anchor) return false;
      // Already in position — nothing to do
      if (container.previousElementSibling === anchor || anchor.nextElementSibling === container) return true;
      anchor.after(container);
      return true;
    }

    // Try immediately in case elements already exist
    let labelDone = applyLabel();
    let relocDone = relocateContainer();

    if (!labelDone || !relocDone) {
      const observer = new MutationObserver(() => {
        if (!labelDone) labelDone = applyLabel();
        if (!relocDone) relocDone = relocateContainer();
        if (labelDone && relocDone) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  })();

  // Event delegation for personalise modal
  document.addEventListener('click', (event) => {
    // Open button
    if (event.target.closest('#personalise-product-btn')) {
      const iframe = DOM.getPersonaliseIframe();
      if (iframe) iframe.src = state.printSystemIframeUrlSrc;
      window.location.hash = '#personalise-product-modal';
      return;
    }

    // Close button
    if (event.target.closest('#personalise-product-modal .close')) {
      window.location.hash = '';
    }
  });

  // Event delegation for large volume quote modal
  document.addEventListener('click', (event) => {
    // Open button
    if (event.target.closest('#custom-large-volume-quote-btn')) {
      window.location.hash = '#large-volume-quote-modal';
      return;
    }

    // Close button
    if (event.target.closest('#large-volume-quote-modal .close')) {
      window.location.hash = '';
    }

    // Close on overlay click (outside popup)
    if (event.target.id === 'large-volume-quote-modal') {
      window.location.hash = '';
    }
  });

  /* =====================================================================
     DISPATCH COUNTDOWN
  ===================================================================== */

  /**
   * Initialize dispatch countdown widget
   * Shows time remaining until 15:00 Madrid time
   */
  function initDispatchCountdown() {
    const widget = document.getElementById('custom-dispatch-countdown-widget');
    if (!widget) return;

    const today = new Date();
    const dow = today.getDay();
    const todayKey = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Hide on weekends or holidays
    if (dow === 0 || dow === 6 || SPANISH_HOLIDAYS.includes(todayKey)) {
      widget.style.display = 'none';
      return;
    }

    const hrsSpan = document.getElementById('custom-dispatch-countdown-hours-span');
    const minsSpan = document.getElementById('custom-dispatch-countdown-minutes-span');
    const secsSpan = document.getElementById('custom-dispatch-countdown-seconds-span');

    if (!hrsSpan || !minsSpan || !secsSpan) return;

    let interval;

    function updateTimer() {
      const now = new Date();

      // Build "today 15:00" in Europe/Madrid, DST-aware
      const madridDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' }); // YYYY-MM-DD
      const cutoff = new Date(`${madridDateStr}T15:00:00`);
      // Convert Madrid local 15:00 to UTC by getting the offset dynamically
      const madridOffset = new Date(
        cutoff.toLocaleString('en-US', { timeZone: 'Europe/Madrid' })
      ) - new Date(cutoff.toLocaleString('en-US', { timeZone: 'UTC' }));
      const cutoffUTC = cutoff.getTime() - madridOffset;

      let diff = cutoffUTC - Date.now();

      if (diff <= 0) {
        widget.style.display = 'none';
        clearInterval(interval);
        return;
      }

      const hrs = Math.floor(diff / 3_600_000);
      diff %= 3_600_000;
      const mins = Math.floor(diff / 60_000);
      diff %= 60_000;
      const secs = Math.floor(diff / 1000);

      hrsSpan.textContent = String(hrs).padStart(2, '0');
      minsSpan.textContent = String(mins).padStart(2, '0');
      secsSpan.textContent = String(secs).padStart(2, '0');
    }

    updateTimer();
    interval = setInterval(updateTimer, 1000);
  }

  /* =====================================================================
     LARGE VOLUME QUOTE PREFILL
  ===================================================================== */

  /**
   * Initialize large volume quote form with SKU prefill
   */
  function initLargeVolumeQuotePrefill() {
    const BTN_SELECTOR = '#custom-large-volume-quote-btn';
    const CUSTOM_ID = 'custom-large-volume-quote-product-sku';
    const HUMAN_NAME = 'Product SKU';
    const FRAME_HINTS = ['form_builder', 'hulk', 'cdn.shopify.com/extensions'];

    // Prevent duplicate binding
    if (document.documentElement.dataset.lvqIframeBind === '1') return;
    document.documentElement.dataset.lvqIframeBind = '1';

    const resolveSku = () => {
      const variant = getSelectedVariant();
      const fallback = document.querySelector('.product-sku__value')?.textContent?.trim() || '';
      return (variant?.sku || fallback).trim();
    };

    const isHulkIframe = (iframe) => {
      if (!iframe || iframe.tagName !== 'IFRAME') return false;
      const src = iframe.getAttribute('src') || '';
      return FRAME_HINTS.some(h => src.includes(h));
    };

    const withPrefill = (rawUrl, sku) => {
      const url = new URL(rawUrl, location.href);
      const params = [
        [`prefill[${CUSTOM_ID}]`, sku],
        [`prefill[${HUMAN_NAME}]`, sku],
        ['prefill[product_sku]', sku],
        ['product_sku', sku],
      ];
      params.forEach(([k, v]) => url.searchParams.set(k, v));

      const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
      params.forEach(([k, v]) => hash.set(k, v));
      url.hash = hash.toString() ? `#${hash.toString()}` : '';

      return url.toString();
    };

    const rewriteIframeSrcWhenReady = (sku, timeoutMs = 10000) => {
      const findFrame = () => Array.from(document.querySelectorAll('iframe')).find(isHulkIframe);

      const frame = findFrame();
      if (frame) {
        frame.setAttribute('src', withPrefill(frame.getAttribute('src') || '', sku));
        return;
      }

      const start = performance.now();
      const observer = new MutationObserver(() => {
        const f = findFrame();
        if (f) {
          observer.disconnect();
          f.setAttribute('src', withPrefill(f.getAttribute('src') || '', sku));
        } else if (performance.now() - start > timeoutMs) {
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    };

    document.addEventListener('click', (e) => {
      if (!e.target.closest(BTN_SELECTOR)) return;
      const sku = resolveSku();
      if (sku) rewriteIframeSrcWhenReady(sku, 12000);
    }, { capture: true });
  }

  /* =====================================================================
     CUSTOM TABS
  ===================================================================== */

  /**
   * Wire up the "View Full Product Specifications" button: open the
   * `<details id="product-specifications-tab">` rendered by the
   * Specifications Tab block and smooth-scroll to it.
   */
  function initCustomTabs() {
    const specButton = document.querySelector('.product-specifications-btn');
    if (!specButton || specButton.dataset.tabsBound === 'true') return;

    specButton.dataset.tabsBound = 'true';

    specButton.addEventListener('click', () => {
      const tab = document.querySelector('#product-specifications-tab');
      if (tab) {
        tab.open = true;
        smoothScrollTo(tab, 1500);
      }
    });
  }

  /* =====================================================================
     MEASUREMENT OPTIONS REORDERING
  ===================================================================== */

  /**
   * Auto-sort radio options that look like measurements (WxH format)
   */
  function autoReorderMeasureFieldsets() {
    const fieldsets = document.querySelectorAll('fieldset.option-selector');
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    Array.from(fieldsets)
      .filter(fs => !fs.closest('.quick-add-drawer'))
      .forEach(fs => {
        const container = fs.querySelector('.option-selector__btns');
        if (!container) return;

        const radios = container.querySelectorAll('input[type="radio"].opt-btn');
        if (!radios.length) return;

        let parsedCount = 0;
        const items = Array.from(radios).map(input => {
          const label = container.querySelector(`label[for="${input.id}"]`);
          const text = (label?.textContent || input.value || '').trim();
          const parsed = parseWidthHeight(input.value) || parseWidthHeight(text);
          if (parsed) parsedCount++;
          return { input, label, text, parsed };
        });

        // Skip if less than half are measurement-like
        if (parsedCount < items.length / 2) return;

        items.sort((a, b) => {
          const pa = a.parsed, pb = b.parsed;
          if (pa && !pb) return -1;
          if (!pa && pb) return 1;
          if (pa && pb) {
            if (pa.first !== pb.first) return pa.first - pb.first;
            if (pa.second !== pb.second) return pa.second - pb.second;
          }
          return collator.compare(a.text, b.text);
        });

        const frag = document.createDocumentFragment();
        items.forEach(({ input, label }) => {
          if (input) frag.appendChild(input);
          if (label) frag.appendChild(label);
        });
        container.appendChild(frag);
      });
  }

  /**
   * Parse "W x H" measurement string
   * @param {string} text 
   * @returns {Object|null}
   */
  function parseWidthHeight(text = '') {
    const match = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(text);
    if (!match) return null;

    const w = parseFloat(match[1].replace(',', '.'));
    const h = parseFloat(match[2].replace(',', '.'));
    return { width: w, height: h, first: w, second: h };
  }

  /* =====================================================================
     UTILITY FUNCTIONS
  ===================================================================== */

  /**
   * Parse price string to number
   * Handles various international formats
   * @param {string} priceText 
   * @returns {number}
   */
  function parsePrice(priceText) {
    if (!priceText) return 0;

    // Extract numeric part
    const match = priceText.match(/\d(?:[\d.,\s\u00A0])*/);
    if (!match) return 0;

    let text = match[0].replace(/\u00A0/g, ' ').replace(/ /g, '');

    // Check for pure thousand groupings (e.g., 1.234 or 1,234)
    if (/^\d{1,3}(?:[.,]\d{3})+$/.test(text)) {
      return parseFloat(text.replace(/[.,]/g, '')) || 0;
    }

    // Determine decimal separator
    const lastDot = text.lastIndexOf('.');
    const lastCom = text.lastIndexOf(',');
    const lastSep = Math.max(lastDot, lastCom);

    if (lastSep !== -1) {
      const fracLen = text.length - lastSep - 1;
      if (fracLen === 1 || fracLen === 2) {
        const dec = text[lastSep];
        const thou = dec === '.' ? ',' : '.';
        text = text.replace(new RegExp('\\' + thou, 'g'), '').replace(dec, '.');
        return parseFloat(text) || 0;
      }
      return parseFloat(text.replace(/[.,]/g, '')) || 0;
    }

    return parseFloat(text) || 0;
  }

  /**
   * Format price with currency symbol
   * @param {number} price 
   * @param {number} decimals 
   * @returns {string}
   */
  function formatPrice(price, decimals = 2) {
    let [intPart, fracPart = ''] = price.toFixed(decimals).split('.');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, state.thousandSeparator);
    const num = decimals ? intPart + state.decimalSeparator + fracPart : intPart;
    return state.currencySymbolPlacement === 'before' 
      ? `${state.currencySymbol} ${num}` 
      : `${num}${state.currencySymbol}`;
  }

  /**
   * Determine if currency symbol comes before or after price
   * @param {string} text 
   * @param {string} symbol 
   * @returns {'before'|'after'}
   */
  function determineCurrencyPlacement(text, symbol) {
    const digitIndex = text.search(/\d/);
    const symbolIndex = text.indexOf(symbol);
    return (symbolIndex !== -1 && symbolIndex < digitIndex) ? 'before' : 'after';
  }

  /**
   * Check if market uses whole-number pricing
   * @param {string} marketId 
   * @returns {boolean}
   */
  function isWholeAmountMarket(marketId) {
    return WHOLE_AMOUNT_MARKETS.has(String(marketId));
  }

  /**
   * Round half up to specified decimals
   * @param {number} n 
   * @param {number} decimals 
   * @returns {number}
   */
  function roundHalfUp(n, decimals = 0) {
    const factor = Math.pow(10, decimals);
    return Math.floor(n * factor + 0.5) / factor;
  }

  /**
   * Count the decimal places present in a numeric string (accepts '.' or ','
   * as the decimal separator). Returns 0 when there is no fractional part.
   * @param {string|number} value
   * @returns {number}
   */
  function countDecimalPlaces(value) {
    const str = typeof value === 'string' ? value : String(value ?? '');
    const match = str.match(/[.,](\d+)\s*$/) || str.match(/[.,](\d+)/);
    return match ? match[1].length : 0;
  }

  /**
   * Clamp a tier price's decimal count to the supported range: 2 decimals is
   * the standard, 4 is the maximum used for special customer pricing.
   * @param {number} count
   * @returns {number}
   */
  function clampTierDecimals(count) {
    return Math.min(4, Math.max(2, count));
  }

  /**
   * Round half up to integer
   * @param {number} n 
   * @returns {number}
   */
  function roundHalfUpToInteger(n) {
    return roundHalfUp(n, 0);
  }

  /**
   * Smooth scroll to element
   * @param {Element} target 
   * @param {number} duration - milliseconds
   */
  function smoothScrollTo(target, duration) {
    const start = window.pageYOffset;
    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
    const end = target.getBoundingClientRect().top + start - headerHeight;
    const distance = end - start;
    const startTime = performance.now();

    const ease = t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    function step(currentTime) {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      window.scrollTo(0, start + distance * ease(progress));
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  /* =====================================================================
     COOKIE HELPERS
  ===================================================================== */

  /**
   * Set cookie
   * @param {string} name 
   * @param {*} value 
   * @param {number} days 
   */
  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    // Convert value to string explicitly to handle booleans
    document.cookie = name + '=' + String(value) + expires + '; path=/';
  }

  /**
   * Get cookie value
   * @param {string} name 
   * @returns {string|null}
   */
  function getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /* =====================================================================
     POLYFILLS
  ===================================================================== */

  /**
   * requestIdleCallback polyfill for Safari
   */
  window.requestIdleCallback = window.requestIdleCallback || function(cb, options) {
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
      });
    }, options?.timeout || 1);
  };

  /* =====================================================================
     START APPLICATION
  ===================================================================== */

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
